<template>
    <article>
        <div class="article-content" id="pdf-content">
            <div class="left-pane">
                <div class="dataContainer">
                    <!-- Unified header for both web and PDF -->
                    <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 10px;">
                        <div class="pdf-page-header"><h2>{{ name }}</h2></div>
                        <h2 class="web-only-title">{{ name }}</h2>
                        <img :src="pdfLogoUrl" class="pdf-logo" alt="LCP Logo" width="125" height="44" />
                        <button v-if="PDF_DOWNLOAD_ENABLED" @click="downloadPDF" class="pdf-btn" data-html2canvas-ignore="true">
                            Download PDF
                        </button>
                    </div>
                    <div v-if="currentDescription" class="description-top">
                        <div class="fig-text-wrapper">
                            {{ capitalize(currentDescription) }}
                        </div>
                    </div>
                    <charts :options="transactionFees" class="chartContainer"></charts>
                    <div class="pdf-page-footer">This information is wholly owned by Lane, Clark &amp; Peacock LLP.</div>
                </div>
            </div>
            <div class="right-pane">
                <div class="fig-text-wrapper">
                    {{ currentDescription }}
                </div>
            </div>
        </div>
    </article>
</template>

<script setup lang="ts">
    // Declare window as global for TypeScript
    declare const window: Window;

    import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
    import axios from 'axios';
    import Highcharts from 'highcharts';
    import { useRoute } from 'vue-router';

    import { useYear } from '../composables/useYear';
    import { BASE_URL, PDF_DOWNLOAD_ENABLED } from '../config';

    interface TransactionCostYear {
        description: string;
        xAxis: string[];
        // series is an array of number arrays (e.g. [[0.29, 0.20, ...], ...])
        series: number[][];
    }

    type PdfDocument = {
      internal: {
        getNumberOfPages: () => number
        pageSize: {
          getWidth: () => number
          getHeight: () => number
        }
      }
      setPage: (pageNumber: number) => void
      setDrawColor: (red: number, green: number, blue: number) => void
      setLineWidth: (width: number) => void
      line: (x1: number, y1: number, x2: number, y2: number) => void
      setFontSize: (size: number) => void
      setTextColor: (red: number, green: number, blue: number) => void
      text: (text: string, x: number, y: number, options?: { align?: string }) => void
    }

    const { selectedYear } = useYear();
    import logoUrl from '@/assets/logo_sector.svg'
    const route = useRoute();

    // Sync year from route
    watch(() => route.params.year, (newYear) => {
        if (newYear && typeof newYear === 'string' && newYear !== selectedYear.value) {
            selectedYear.value = newYear;
        }
    }, { immediate: true });

    const name = ref('Transaction costs');
    const chartData = ref<Record<string, TransactionCostYear>>({});
    const loaded = ref(false);

    // Responsive state for screen width detection
    const isSmallScreen = ref(false)
    const isPdfExporting = ref(false)
    const pdfLogoUrl = ref(logoUrl)
    const pdfLogoPngCache = ref<string | null>(null)

    const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1)

    // Computed property for responsive margin bottom
    const responsiveMarginBottom = computed(() => {
        if (isPdfExporting.value) return 180
        return isSmallScreen.value ? 120 : 120
    })

    // Screen width detection
    const updateScreenSize = () => {
      const width = window.innerWidth
      isSmallScreen.value = (width <= 1306 && width >= 824) || width < 715
    }

    const currentDescription = computed(() => {
        const yearData = chartData.value[selectedYear.value]
        return yearData ? yearData.description : ''
    })

    const transactionFees = computed(() => {
        const yearData = chartData.value[selectedYear.value] || { xAxis: [], series: [] };

        return {
            chart: {
                type: 'boxplot',
                marginTop: 20,
                marginLeft: isPdfExporting.value ? 120 : 100,
                marginBottom: responsiveMarginBottom.value,
            },
            title: { text: '' },
            xAxis: {
                categories: yearData.xAxis,
                title: { text: 'Asset class' },
                labels: {
                    useHTML: !isPdfExporting.value, // Disable HTML for PDF to avoid clipping
                    rotation: -45, // Rotate labels
                    y: isPdfExporting.value ? 12 : undefined, // Shift labels down during PDF to avoid clipping at axis line
                    style: {
                        // Disable width constraint and ellipsis for PDF to show full text
                        width: isPdfExporting.value ? undefined : '100px',
                        textOverflow: isPdfExporting.value ? 'none' : 'ellipsis',
                        whiteSpace: 'nowrap' // Ensures text stays on one line
                    },
                    formatter: function (this: Highcharts.AxisLabelsFormatterContextObject): string {

                      // role="tooltip" identifies the popup semantically
                      // tabindex="0" makes it focusable for keyboard/mobile users
                      return `
                          <span class="label-anchor">
                              ${this.value}
                          </span>`;
                              }
                          }
            },
            yAxis: {
                title: { text: 'Transaction costs' },
                labels: {
                    formatter(this: Highcharts.AxisLabelsFormatterContextObject): string {
                        const value = parseFloat(this.value as string).toFixed(2);
                        return `${value}%`;
                    },
                    y: -3,
                    x: 1,
                    style: {
                        color: '#141e34',
                    }
                },
                tickInterval: 0.2,
            },
            tooltip: {
                valueSuffix: '%',
                valueDecimals: 2,
                pointFormat: '<b>Top quartile: {point.high}</b><br/><b>Median: {point.median}</b><br/><b>Bottom quartile: {point.low}</b>',
            },
            legend: { enabled: false },
            credits: { enabled: false },
            series: [{
                name: 'Transaction Fees',
                data: yearData.series
            }],
        };
    });

    // Lifecycle
    onMounted(async () => {
      // Initialize screen size
      updateScreenSize()
      // Add resize listener
      window.addEventListener('resize', updateScreenSize)

      try {
          const { data } = await axios.get('/data/transaction_costs.json');
          chartData.value = data;
          loaded.value = true;
      } catch (e) {
          console.error('Failed to load transaction costs', e);
      }
    })

    onUnmounted(() => {
      // Clean up resize listener
      window.removeEventListener('resize', updateScreenSize)
    })

    // Watch for responsive margin changes
    watch(responsiveMarginBottom, () => {
      // Chart will automatically update due to computed property
    })

    import { useHead } from '@unhead/vue';

    const pageTitle = computed(() => name.value);
    const pdfFooterText = 'This information is wholly owned by Lane, Clark & Peacock LLP.'

    // PDF Export
    const getPdfLogoSrc = async () => {
      if (pdfLogoPngCache.value) {
        return pdfLogoPngCache.value
      }

      return new Promise<string>((resolve) => {
        const img = new Image()
        img.crossOrigin = 'anonymous'

        img.onload = () => {
          const canvas = document.createElement('canvas')
          canvas.width = img.naturalWidth || 125
          canvas.height = img.naturalHeight || 44

          const ctx = canvas.getContext('2d')
          if (!ctx) {
            resolve(logoUrl)
            return
          }

          ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
          const pngDataUrl = canvas.toDataURL('image/png')
          pdfLogoPngCache.value = pngDataUrl
          resolve(pngDataUrl)
        }

        img.onerror = () => resolve(logoUrl)
        img.src = logoUrl
      })
    }

        const addPdfFooters = (pdf: PdfDocument) => {
          const pageCount = pdf.internal.getNumberOfPages()
          const pageWidth = pdf.internal.pageSize.getWidth()
          const pageHeight = pdf.internal.pageSize.getHeight()
          const horizontalInset = 10
          const textY = pageHeight - 12
          const lineY = textY - 3

          for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
            pdf.setPage(pageNumber)
            pdf.setDrawColor(204, 204, 204)
            pdf.setLineWidth(0.2)
            pdf.line(horizontalInset, lineY, pageWidth - horizontalInset, lineY)
            pdf.setFontSize(7)
            pdf.setTextColor(102, 102, 102)
            pdf.text(pdfFooterText, pageWidth / 2, textY, { align: 'center' })
          }
        }

    const downloadPDF = async () => {
        const element = document.getElementById('pdf-content');
        if (!element) return;

      pdfLogoUrl.value = await getPdfLogoSrc()
        isPdfExporting.value = true
        // Add class to force mobile/single-column layout for PDF
        element.classList.add('pdf-mode');

        // Wait for fonts and highcharts redraw
        await Promise.all([
          document.fonts.ready,
          new Promise(r => setTimeout(r, 1000))
        ]);

        const options = {
            margin:       10,
            filename:     'transaction_costs_report.pdf',
            image:        { type: 'jpeg' as const, quality: 0.98 },
            html2canvas:  { scale: 2, useCORS: true, windowWidth: 800 },
            jsPDF:        { unit: 'mm' as const, format: 'a4' as const, orientation: 'landscape' as const },
            pagebreak:    { mode: ['avoid-all', 'css', 'legacy'] }
        };

          const { default: html2pdf } = await import('html2pdf.js');
          const worker = html2pdf().set(options).from(element).toPdf();
          const pdf = await worker.get('pdf');
          addPdfFooters(pdf);
          await worker.save();

        // Remove the class to restore normal layout
        isPdfExporting.value = false
        pdfLogoUrl.value = logoUrl
        element.classList.remove('pdf-mode');
    }

    useHead({
        title: pageTitle,
        meta: [
            { name: 'description', content: currentDescription },
            { property: 'og:title', content: () => `Fee Survey: ${pageTitle.value}` },
            { property: 'og:description', content: currentDescription },
            { property: 'og:image', content: `${BASE_URL}/images/ifs-social.png` },
            { name: 'twitter:card', content: 'summary_large_image' },
            { name: 'twitter:title', content: () => `Fee Survey: ${pageTitle.value}` },
            { name: 'twitter:description', content: currentDescription },
            { name: 'twitter:image', content: `${BASE_URL}/images/ifs-social.png` }
        ],
        script: [
            {
                type: 'application/ld+json',
                innerHTML: computed(() => JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "Dataset",
                    "name": `LCP Fee Survey - ${pageTitle.value}`,
                    "description": currentDescription.value,
                    "provider": {
                        "@type": "Organization",
                        "name": "Lane Clark & Peacock LLP"
                    },
                    "variableMeasured": "Transaction Costs Summary of a selection of key asset classes"
                }))
            }
        ]
    })
</script>

<style lang="scss">
/* 1. THE ANCHOR (The x-axis label text) */

.highcharts-xaxis-labels span:hover,
.highcharts-xaxis-labels span:focus,
.highcharts-xaxis-labels span:active {
    visibility: visible !important;
    opacity: 1 !important;
    overflow: visible !important;
    z-index: 10000 !important;
    width: max-content !important;
}

.highcharts-xaxis-labels span {
  z-index: 10000 !important;
}

.label-anchor {
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  color: #17355e;
}

/* 2. THE TOOLTIP (Breaking out of overflow:hidden) */
/*
.label-tooltip {

    position: fixed;
    position-area: left center;
    position-try: flip-block;


    visibility: hidden;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease;

    width: max-content;
    min-width: max-content;
    white-space: nowrap;
    overflow: visible;


    background: #333;
    color: #fff;
    padding: 6px 10px;
    border-radius: 4px;
    font-size: 12px;
    white-space: nowrap;
    z-index: 10000;
}

/* 3. SHOW LOGIC (Desktop Hover + Mobile/Keyboard Focus) */
/*
.label-anchor:hover + .label-tooltip,
.label-anchor:focus + .label-tooltip,
.label-anchor:active + .label-tooltip {
    visibility: visible;
    opacity: 1;
}
*/
/* 4. ACCESSIBILITY FOCUS RING */
.label-anchor:focus-visible {
    outline: 2px solid #2196F3;
    outline-offset: 2px;
}
    .highcharts-boxplot-box {
        fill: #F0F0E0;
        display: none;
    }
    .highcharts-boxplot-median {
        stroke: #faa743;
        stroke-width: 3px;
    }
    .highcharts-boxplot-whisker {
        stroke: #00a5c9;
        stroke-width: 4px;
    }
    .highcharts-boxplot-stem {
        stroke: #93b1b6;
        stroke-width: 4px;
    }
    @media only screen and (max-width: 1024px) {
        .highcharts-container, .highcharts-root {
            width: 100% !important;
        }
    }
</style>

<style lang="scss" scoped>
    .pdf-logo {
      display: none;
    }

    .pdf-btn {
      background-color: var(--lcp-blue, #17355e);
      color: white;
      border: none;
      border-radius: 5px;
      padding: 8px 16px;
      font-size: 0.9rem;
      font-weight: 600;
      cursor: pointer;
      transition: background-color 0.2s;

      &:hover {
        background-color: var(--investment-violet, #00a5c9);
      }
    }

    // Description shown at top only during PDF mode
    .description-top {
      display: none;
      margin-bottom: 20px;
    }

    // PDF mode: force single-column mobile layout
    .pdf-mode {
      display: block !important;
      overflow: hidden !important;
      font-family: var(--montserrat) !important;

      .pdf-logo {
        display: block !important;
        max-width: 125px !important;
        height: auto !important;
        flex-shrink: 0 !important;
      }

      .left-pane, .right-pane {
        width: 100% !important;
        padding: 0 !important;
      }

      // Hide the sidebar description (it's duplicated at the top)
      .right-pane {
        display: none !important;
      }

      // Show the top description instead
      .description-top {
        display: block !important;
      }

      // Body font size for PDF
      font-size: 10pt !important;
      line-height: 14pt !important;

      p {
        font-size: 10pt !important;
        line-height: 14pt !important;
        margin-top: 0 !important;
        margin-bottom: 2pt !important;
      }

      h1 {
        font-size: 18pt !important;
        line-height: 22pt !important;
        margin-top: 0 !important;
        margin-bottom: 5px !important;
      }

      h2 {
        font-size: 14pt !important;
        line-height: 18pt !important;
        margin-top: 0 !important;
        margin-bottom: 5px !important;
      }

      // Description styling in PDF
      .fig-text-wrapper, .description-top .fig-text-wrapper {
        font-size: 10pt !important;
        padding-left: 0 !important;

        &::before {
          display: none !important;
        }

        &::first-letter {
          text-transform: uppercase !important;
        }
      }

      .chartContainer {
        width: 900px !important;
        min-height: 400px !important;
        padding: 0 !important;
        margin: 0 !important;
        box-shadow: none !important;
        border-radius: 0 !important;
      }

      .dataContainer {
        margin: 0 !important;
        padding: 0 !important;
        min-height: 180mm !important;
        display: flex !important;
        flex-direction: column !important;
        break-inside: avoid-page !important;
        page-break-inside: avoid !important;
      }

      &.article-content {
        padding: 10px !important;
      }

      :deep(.tableArea) {
        margin: 0 !important;
        padding: 0 !important;
        overflow-x: visible !important;
      }

      // Increase chart height for readability
      :deep(.highcharts-container),
      :deep(.highcharts-root) {
        height: 400px !important;
        overflow: visible !important;
      }

      // Ensure labels aren't clipped
      :deep(.highcharts-axis-labels) {
        overflow: visible !important;
      }

      :deep(.highcharts-xaxis-labels span) {
        width: auto !important;
        text-overflow: clip !important;
        white-space: nowrap !important;
      }

      // Remove internal Highcharts spacing if possible via CSS
      :deep(.highcharts-root) {
        padding: 0 !important;
        margin: 0 !important;
      }

      // Shrink table font so all columns fit on A4 landscape
      :deep(td),
      :deep(th) {
        font-size: 0.55rem !important;
        padding: 2px 2px !important;
      }

      :deep(td:first-child),
      :deep(th:first-child) {
        min-width: auto !important;
      }

      // Force white background on tables
      :deep(tr),
      :deep(tbody tr td:first-child),
      :deep(thead tr th:first-child) {
        background-color: #ffffff !important;
      }

      // Hide mobile scroll hint in PDF
      :deep(.mobile-scroll-hint) {
        display: none !important;
      }

      // Show per-page heading
      .pdf-page-header {
        display: block !important;
      }

      // Show per-page footer
      .pdf-page-footer {
        display: none !important;
      }

      // Hide web-only title in PDF
      .web-only-title {
        display: none !important;
      }
    }

    // Web-only title (hidden in PDF)
    .web-only-title {
      display: block;
    }

    // Hidden by default (only shown in pdf-mode)
    .pdf-page-header {
      display: none;

      h2 {
        font-size: 1.2rem;
        color: var(--lcp-blue);
        margin-top: 0 !important;
        margin-bottom: 5px !important;
        padding: 0 0 15px 0 !important;
        position: relative !important;
        display: inline-block !important;

        &::before, &::after {
          display: block !important;
        }
      }
    }

    .pdf-page-footer {
      display: none;
      margin-top: 5px !important;
      padding-top: 5px !important;
      border-top: 1px solid #ccc;
      font-size: 0.65rem;
      color: #666;
      text-align: center;
    }
    .dataContainer {
        margin: 0 0 60px 0;
    }
    .dataContainer:last-child {
        margin-bottom: 0;
    }
    .chartContainer {
        margin: 0;
        border-radius: 20px;
        padding: 20px;
        background-color: #fff;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }
    #highcharts {
            height: calc(100dvh - 100px) !important;
    }
    .rightGutter {
        margin: 5% 0 0 0;
        /*border: 3px solid lightgreen;*/
        width: 20%;
        display: inline-block;
        vertical-align: top;
    }
    p {
        text-align: left;
        font-size: 0.8em;
        line-height: 1.2em;
    }
    ul {
        list-style-type: none;
        padding: 0;
    }
    li {
        display: block;
        margin: 0 10px;
    }
    a {
        color: #42b983;
    }
    .table {
        width: 100%;
        max-width: 100%;
        /*margin-left: 7%;*/
    }
    .tableArea {
            overflow-x: auto;
    }
    table {
        border-collapse: collapse;
        border-spacing: 0;
    }
    tr:nth-child(2) {
        td:first-child {
            border: none;
        }
        td {
            border-top: 2px solid #17355e;
            padding-top: 10px;
        }
    }
    tr {
        td:first-child {
            border: none;
            text-align: right;
            padding-right: 10px;
            min-width: 200px;
        }
        td {
            border-bottom: 2px solid #17355e;
            padding-left: 4px;
            padding-right: 4px;
        }
    }
    td {
        text-align: left;
        padding-top: .5em;
        padding-bottom: .5em;
        font-size: 0.9rem;
    }
    h2 {
        font-size: 1.5rem;
        font-weight: 700;
        padding: 0 0 20px 0;
        color: var(--lcp-blue);
        position: relative;
        margin-bottom: 20px;
        display: inline-block;

        // Horizontal underline with fade - responsive to text width
        &::before {
            content: '';
            position: absolute;
            bottom: 8px;
            left: 0;
            width: calc(100% - 10px);
            height: 3px;
            background: linear-gradient(to right,
                var(--investment-violet) 0%,
                var(--investment-violet) 50%,
                transparent 100%);
            border-radius: 2px;
        }

        // Small accent dot at start
        &::after {
            content: '';
            position: absolute;
            bottom: 5px;
            left: 0px;
            width: 9px;
            height: 9px;
            background: var(--investment-violet);
            border-radius: 50%;
        }
    }
    h3 {
        font-size: 1.25rem;
        font-weight: 700;
    }
    h3 {
        font-size: 1.1rem;
    }
    .article-content {
        flex-wrap: wrap;
        padding: 25px;
        display: flex;
    }
    .left-pane {
        padding: 0 20px 0 0;
        width: 75%;
    }
    .right-pane {
        width: 25%;
    }
    figcaption {
        display: flex;
        align-items: flex-start;
        position: relative;
        padding: 0 0 0 20px;
    }
    .fig-text-wrapper {
        color: var(--lcp-blue);
        position: relative;
        padding: 0 0 0 20px;

        &::first-letter {
            text-transform: uppercase;
        }

        &::before{
            font-family: var(--font-awesome-font);
            font-weight: 900;
            content: "\2b";
            display: inline-block;
            text-rendering: auto;
            -webkit-font-smoothing: antialiased;
            font-size: 1.4rem;
            position: absolute;
            left: 0px;
            top: -13px;
            color: var(--investment-violet);
        }
    }
    tr:nth-child(3) td .circle {
        float: right;
        background: rgb(0, 165, 201);
        width: 14px;
        height: 14px;
        border-radius: 100px;
        margin: 2px 2px 0 10px;
    }
    tr:nth-child(4) td .circle {
        float: right;
        background: rgb(251, 166, 28);
        width: 14px;
        height: 14px;
        border-radius: 100px;
        margin: 2px 2px 0 10px;
    }
    tr:nth-child(5) td .circle {
        float: right;
        background: rgb(23, 53, 94);
        width: 14px;
        height: 14px;
        border-radius: 100px;
        margin: 2px 2px 0 10px;
    }
    tr:nth-child(6) td .circle {
        float: right;
        background: rgb(190, 199, 200);
        width: 14px;
        height: 14px;
        border-radius: 100px;
        margin: 2px 2px 0 10px;
    }

    // Responsive styles
    @mixin responsive-layout {
      .left-pane, .right-pane {
        width: 100% !important;
        padding: 0;
      }

      .right-pane {
        margin-top: 20px;
      }

      .article-content {
        width: 100% !important;
        padding: 15px;
        display: block;
        margin: 0;
        overflow: hidden;
      }

      .chartContainer, .highcharts-container {
        width: 100% !important;
      }
    }

    @media only screen and (min-width: 821px) and (max-width: 1024px) {
      .article-content {
        display: block;
      }
      .left-pane {
        padding-right: 0;
        width: 100%;
      }
      .right-pane {
        padding-top: 20px;
        width: 100%;
      }
    }

    @media only screen and (max-width: 820px) {
      article {
        min-width: 100%;
        display: block;
        overflow-y: scroll;
        -webkit-overflow-scrolling: touch;
      }

      @include responsive-layout;

      .fa-plus {
        min-width: 25px;
        padding: 3px 8px 0 0;
      }
    }

    @media only screen and (max-width: 768px) {
      .tableArea {
        overflow-x: auto;
      }

      h3, .table {
        width: 100%;
        margin-left: 0;
      }

      .left-menu {
        display: block;
        position: absolute;
        left: -1000px;
        z-index: 100;

        &.active {
          left: 0;
        }
      }
    }
</style>
